import { useState, useEffect, useMemo } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Label,
} from "recharts";
import {
  useGetAllCombinedMaintenanceCostQuery,
  useGetAverageResponseTimeQuery,
} from "../../slices/maintenanceApiSlice";
import { useGetAcademyQuery } from "../../slices/userApiSlice";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import "./../../pages/adminPages/css/MintenanceGraphs.css";

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MaintenanceGraphs = ({
  selectedAcademyExternal,
  selectedYearExternal,
  hideControls = false,
}) => {
  const [internalSelectedAcademy, setInternalSelectedAcademy] = useState("");
  const [internalSelectedYear, setInternalSelectedYear] = useState("");

  const { token } = useSelector((state) => state.auth);

  // Determine effective selections
  const selectedAcademy = selectedAcademyExternal || internalSelectedAcademy;
  const selectedYear = selectedYearExternal || internalSelectedYear;

  // If external controls are used, usually we hide internal ones.
  // We infer usage of external controls if props are passed.
  const isControlled = !!selectedAcademyExternal;

  const {
    data: maintenanceData,
    isLoading: loadingMaintenance,
    error: errorMaintenance,
  } = useGetAllCombinedMaintenanceCostQuery();

  const {
    data: academies,
    isLoading: loadingAcademies,
    error: errorAcademies,
  } = useGetAcademyQuery();

  const {
    data: responseTimeData,
    isLoading: loadingResponse,
    error: errorResponse,
  } = useGetAverageResponseTimeQuery();

  const isLoadingAll =
    token && (loadingAcademies || loadingMaintenance || loadingResponse);
  const hasError = errorAcademies || errorMaintenance || errorResponse;

  useEffect(() => {
    if (isLoadingAll) {
      // Toast handles repeated calls? Assuming yes or parent manages it.
      // But actually, showing global toast here might be annoying if parent also shows it.
      // Keeping it for now as per original behavior.
      // toast.loading("Loading data, please wait...");
    } else {
      toast.dismiss();
    }
    return () => toast.dismiss();
  }, [isLoadingAll]);

  useEffect(() => {
    if (!isLoadingAll && hasError) {
      // toast.error("Error", {
      //   description: "Failed to load data from server.",
      // });
    }
  }, [hasError, isLoadingAll]);

  const validAcademyIds = useMemo(() => {
    return new Set((academies || []).map((a) => a.academyId));
  }, [academies]);

  const filteredMaintenanceData = useMemo(() => {
    if (!maintenanceData) return [];
    return maintenanceData.filter(
      (item) => item.academyId && validAcademyIds.has(item.academyId),
    );
  }, [maintenanceData, validAcademyIds]);

  const academyIdToName = useMemo(() => {
    return (academies || []).reduce((acc, curr) => {
      acc[curr.academyId] = curr.name;
      return acc;
    }, {});
  }, [academies]);

  const academyData = useMemo(() => {
    return filteredMaintenanceData.reduce((acc, curr) => {
      const { academyId, year, month, totalCost } = curr;
      const academyName = academyIdToName[academyId];
      if (!acc[academyId]) acc[academyId] = { name: academyName, years: {} };
      if (!acc[academyId].years[year])
        acc[academyId].years[year] = Array(12).fill(0);
      acc[academyId].years[year][month - 1] += totalCost;
      return acc;
    }, {});
  }, [filteredMaintenanceData, academyIdToName]);

  // Combine years logic
  const allYears = useMemo(() => {
    const yearsSet = new Set();
    // From maintenance
    if (academyData[selectedAcademy]) {
      Object.keys(academyData[selectedAcademy].years).forEach((year) =>
        yearsSet.add(year),
      );
    }
    // From response time
    (responseTimeData || []).forEach((entry) => {
      if (entry.academyId === selectedAcademy) {
        yearsSet.add(String(entry.year));
      }
    });
    return Array.from(yearsSet).sort((a, b) => b - a);
  }, [selectedAcademy, academyData, responseTimeData]);

  // Internal State Initialization (Only if not controlled)
  useEffect(() => {
    if (!isControlled) {
      if (academies && academies.length > 0 && !internalSelectedAcademy) {
        setInternalSelectedAcademy(academies[0].academyId);
      }
    }
  }, [academies, internalSelectedAcademy, isControlled]);

  useEffect(() => {
    if (!isControlled) {
      if (internalSelectedAcademy && allYears.length > 0) {
        // Maintain specific year selection logic if needed, 
        // but resetting to first available is safer when academy changes
        if (!internalSelectedYear || !allYears.includes(internalSelectedYear)) {
          setInternalSelectedYear(allYears[0]);
        }
      }
    }
  }, [internalSelectedAcademy, allYears, internalSelectedYear, isControlled]);


  if (isLoadingAll || !academies || !maintenanceData || !responseTimeData) {
    return <div className="h-[300px] flex items-center justify-center text-muted-foreground">Loading charts...</div>;
  }

  const selectedAcademyData = academyData[selectedAcademy];
  const selectedYearCostData =
    selectedAcademyData?.years[selectedYear] || Array(12).fill(0);

  const filteredResponseTime = (responseTimeData || []).filter(
    (entry) =>
      entry.academyId === selectedAcademy &&
      String(entry.year) === String(selectedYear),
  );

  const responseTimePerMonth = Array(12).fill(null);
  filteredResponseTime.forEach((entry) => {
    responseTimePerMonth[entry.month - 1] = entry.averageResponseTimeHours;
  });

  const mappedData = months.map((month, index) => ({
    month,
    cost: selectedYearCostData[index],
    responseTime: responseTimePerMonth[index] ?? 0,
  }));

  return (
    <div className="graph-container">
      {!isControlled && !hideControls && (
        <>
          <h2 className="graph-title">Simple Analysis</h2>
          <div className="flex flex-wrap gap-4 mb-6">
            <Select
              value={internalSelectedAcademy}
              onValueChange={(val) => {
                setInternalSelectedAcademy(val);
                setInternalSelectedYear(""); // Reset year on academy change
              }}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select Academy" />
              </SelectTrigger>
              <SelectContent>
                {academies.map((a) => (
                  <SelectItem key={a.academyId} value={a.academyId}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={internalSelectedYear}
              onValueChange={setInternalSelectedYear}
              disabled={!internalSelectedAcademy}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {allYears.map((year) => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      <div className="graph-grid">
        <div className="graph-card">
          <h3 className="graph-subtitle">Maintenance Cost</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={mappedData}>
              <XAxis tick={{ fontSize: 12 }} dataKey="month">
                <Label value="Month" offset={-5} position="insideBottom" />
              </XAxis>
              <YAxis tick={{ fontSize: 12 }}>
                <Label
                  value="Cost (Nu)"
                  angle={-90}
                  position="center"
                  dx={-20}
                />
              </YAxis>
              <Tooltip
                formatter={(value) => [`Nu. ${value}`, "Cost"]}
                labelStyle={{ color: "black" }}
              />
              <Bar dataKey="cost" fill="#897463" radius={[5, 5, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="graph-card">
          <h3 className="graph-subtitle">Average Response Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={mappedData}>
              <XAxis tick={{ fontSize: 12 }} dataKey="month">
                <Label value="Month" offset={-5} position="insideBottom" />
              </XAxis>
              <YAxis tick={{ fontSize: 12 }}>
                <Label
                  value="Response Time (hrs)"
                  angle={-90}
                  position="center"
                  dx={-20}
                />
              </YAxis>
              <Tooltip
                formatter={(value) => [`${value} hrs`, "Response Time"]}
                labelStyle={{ color: "black" }}
              />
              <Line
                type="monotone"
                dataKey="responseTime"
                stroke="#315845"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceGraphs;
